import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * ScheduledTasksPage — P1-NF19.
 *
 * Operator surface for the P1-NF17/NF18 scheduled-tasks subsystem
 * (`/v1/admin/scheduled-tasks`). The page lets an operator:
 *
 *   - browse the platform's scheduled_tasks rows, filtered by source /
 *     target_host / enabled,
 *   - create a new UI-owned task,
 *   - edit any task (a PATCH on a deploy_script_managed=true row
 *     auto-flips the flag on the server — UI shows the ownership
 *     change),
 *   - delete a task,
 *   - flip the enabled bit inline,
 *   - run a task right now (POST /:id/run-now),
 *   - kick the host reconciler to apply pending changes (POST
 *     /reconcile-now).
 *
 * Hand-laid systemd units on the host are NOT surfaced here — only ones
 * managed by Nexus. The Reconcile-now button is the only thing that
 * mutates host state directly; everything else is row-level edits that
 * the reconciler picks up on its next pass.
 *
 *   ┌────── filters ─────── refresh · reconcile · new ─────────────────┐
 *   │ name · schedule · target host · state · owner · last run · acts  │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * State badge (computed client-side, see deriveState below):
 *   disabled (gray) — !enabled
 *   never    (gray) — lastRunAt === null
 *   running  (blue) — lastRunAt && lastRunOk === null
 *   failed   (red)  — lastRunOk === false
 *   stale    (yellow) — ok but lastRunAt > 36h ago
 *   ok       (green) — otherwise
 *
 * The page re-renders relative timestamps on a 1s ticker so "3h ago"
 * ages naturally without re-fetching (mirror of AuditLogPage).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NexusAdminApiError, useNexusFetch } from '../api.js';
const SOURCE_FILTER_OPTIONS = [
    { value: 'all', label: 'All sources' },
    { value: 'deploy_script', label: 'deploy_script' },
    { value: 'ui', label: 'ui' },
    { value: 'migrated', label: 'migrated' },
];
function emptyDraft() {
    return {
        name: '',
        schedule: '',
        command: '',
        targetHost: '',
        workingDirectory: '',
        envText: '',
        userName: '',
        description: '',
        enabled: true,
    };
}
function envRecordToText(env) {
    if (!env)
        return '';
    return Object.entries(env)
        .map(([k, v]) => `${k}=${v}`)
        .join('\n');
}
function taskToDraft(t) {
    return {
        name: t.name,
        schedule: t.schedule,
        command: t.command,
        targetHost: t.targetHost,
        workingDirectory: t.workingDirectory ?? '',
        envText: envRecordToText(t.env),
        userName: t.userName ?? '',
        description: t.description ?? '',
        enabled: t.enabled,
    };
}
/**
 * Parse the env textarea. Each non-empty, non-comment line must match
 * `KEY=VALUE`. Returns either { ok: true, env } or { ok: false, error }.
 * Empty textarea yields { ok: true, env: null } so we can send null on
 * PATCH to clear the column.
 */
function parseEnvText(raw) {
    const trimmed = raw.trim();
    if (!trimmed)
        return { ok: true, env: null };
    const out = {};
    const lines = raw.split(/\r?\n/);
    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i] ?? '';
        const stripped = line.trim();
        if (!stripped || stripped.startsWith('#'))
            continue;
        const eq = stripped.indexOf('=');
        if (eq <= 0) {
            return {
                ok: false,
                error: `Line ${i + 1}: expected KEY=VALUE (got "${stripped}").`,
            };
        }
        const key = stripped.slice(0, eq).trim();
        const value = stripped.slice(eq + 1);
        if (!key) {
            return { ok: false, error: `Line ${i + 1}: empty key.` };
        }
        out[key] = value;
    }
    return { ok: true, env: out };
}
// Server-side regex from NF18 — kept identical so we never drift.
const NAME_RE = /^[a-z0-9][a-z0-9._-]{1,99}$/i;
function isValidName(name) {
    return NAME_RE.test(name);
}
const STALE_MS = 36 * 3600 * 1000;
function deriveState(t, nowMs) {
    if (!t.enabled)
        return 'disabled';
    if (t.lastRunAt === null)
        return 'never';
    const lastMs = Date.parse(t.lastRunAt);
    if (t.lastRunOk === null)
        return 'running';
    if (t.lastRunOk === false)
        return 'failed';
    if (Number.isFinite(lastMs) && nowMs - lastMs > STALE_MS)
        return 'stale';
    return 'ok';
}
const STATE_BADGE_CLASS = {
    disabled: 'bg-secondary',
    never: 'bg-secondary',
    running: 'bg-info text-dark',
    failed: 'bg-danger',
    stale: 'bg-warning text-dark',
    ok: 'bg-success',
};
function StateBadgeView({ state }) {
    return _jsx("span", { className: `badge ${STATE_BADGE_CLASS[state]}`, children: state });
}
// ---------------------------------------------------------------------
// Owner icons + labels.
const OWNER_LABEL = {
    deploy_script: 'deploy_script',
    ui: 'ui',
    migrated: 'migrated',
};
function OwnerCell({ source }) {
    // Inline SVGs so we don't require bootstrap-icons as a runtime asset.
    const icon = source === 'deploy_script' ? (
    // bi-lock-fill
    _jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 16 16", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" }) })) : source === 'ui' ? (
    // bi-person-fill
    _jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 16 16", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" }) })) : (
    // bi-arrow-right
    _jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 16 16", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { fillRule: "evenodd", d: "M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z" }) }));
    return (_jsxs("span", { className: "d-inline-flex align-items-center gap-1 small", children: [_jsx("span", { className: "text-muted", children: icon }), OWNER_LABEL[source]] }));
}
// ---------------------------------------------------------------------
// Relative-time helper (mirror of AuditLogPage).
function formatRelative(fromIso, now) {
    const then = Date.parse(fromIso);
    if (Number.isNaN(then))
        return fromIso;
    const deltaMs = now - then;
    if (deltaMs < 0)
        return 'just now';
    const sec = Math.floor(deltaMs / 1000);
    if (sec < 5)
        return 'just now';
    if (sec < 60)
        return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60)
        return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24)
        return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days === 1)
        return 'yesterday';
    if (days < 7)
        return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5)
        return `${weeks}w ago`;
    const months = Math.floor(days / 30);
    if (months < 12)
        return `${months}mo ago`;
    const years = Math.floor(days / 365);
    return `${years}y ago`;
}
function formatDuration(ms) {
    if (!Number.isFinite(ms) || ms < 0)
        return '—';
    if (ms < 1000)
        return `${ms}ms`;
    if (ms < 60 * 1000)
        return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)}s`;
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}m${secs.toString().padStart(2, '0')}s`;
}
function draftToCreate(d, env) {
    const out = {
        name: d.name.trim(),
        schedule: d.schedule.trim(),
        command: d.command.trim(),
        targetHost: d.targetHost.trim(),
        enabled: d.enabled,
    };
    if (d.workingDirectory.trim())
        out.workingDirectory = d.workingDirectory.trim();
    if (env !== null)
        out.env = env;
    if (d.userName.trim())
        out.userName = d.userName.trim();
    if (d.description.trim())
        out.description = d.description.trim();
    return out;
}
function draftToPatch(d, env, orig) {
    const patch = {};
    if (d.name.trim() !== orig.name)
        patch.name = d.name.trim();
    if (d.schedule.trim() !== orig.schedule)
        patch.schedule = d.schedule.trim();
    if (d.command.trim() !== orig.command)
        patch.command = d.command.trim();
    if (d.targetHost.trim() !== orig.targetHost)
        patch.targetHost = d.targetHost.trim();
    const wd = d.workingDirectory.trim() || null;
    if (wd !== orig.workingDirectory)
        patch.workingDirectory = wd;
    const origEnvText = envRecordToText(orig.env);
    if (envRecordToText(env) !== origEnvText)
        patch.env = env;
    const un = d.userName.trim() || null;
    if (un !== orig.userName)
        patch.userName = un;
    const desc = d.description.trim() || null;
    if (desc !== orig.description)
        patch.description = desc;
    if (d.enabled !== orig.enabled)
        patch.enabled = d.enabled;
    return patch;
}
function errorFieldKey(err) {
    const raw = err.path ?? err.field ?? '';
    const segs = raw.replace(/^\/+/, '').split(/[./]/).filter(Boolean);
    const last = segs[segs.length - 1];
    switch (last) {
        case 'name':
            return 'name';
        case 'schedule':
            return 'schedule';
        case 'command':
            return 'command';
        case 'targetHost':
            return 'targetHost';
        case 'workingDirectory':
            return 'workingDirectory';
        case 'env':
            return 'envText';
        case 'userName':
            return 'userName';
        case 'description':
            return 'description';
        case 'enabled':
            return 'enabled';
        default:
            return null;
    }
}
// ---------------------------------------------------------------------
// Component.
export function ScheduledTasksPage() {
    const fetcher = useNexusFetch();
    // ---- Filters ------------------------------------------------------
    const [sourceFilter, setSourceFilter] = useState('all');
    const [hostFilter, setHostFilter] = useState('');
    const [enabledFilter, setEnabledFilter] = useState('all');
    // ---- Data ---------------------------------------------------------
    const [tasks, setTasks] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [reconciling, setReconciling] = useState(false);
    const [reconcileSummary, setReconcileSummary] = useState(null);
    const [toast, setToast] = useState(null);
    // 1s ticker so "3m ago" labels age. Same trick as AuditLogPage.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, []);
    const [editModal, setEditModal] = useState({ kind: 'closed' });
    const [draft, setDraft] = useState(emptyDraft);
    const [submitting, setSubmitting] = useState(false);
    const [formBanner, setFormBanner] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [generalErrors, setGeneralErrors] = useState([]);
    const [runModal, setRunModal] = useState({ kind: 'closed' });
    const [deleteModal, setDeleteModal] = useState({ kind: 'closed' });
    const [reconcileModal, setReconcileModal] = useState({
        kind: 'closed',
    });
    // Inline per-row "Enable toggle pending" markers.
    const [togglingIds, setTogglingIds] = useState(() => new Set());
    // ---- Load ---------------------------------------------------------
    const loadTasks = useCallback(async () => {
        setLoadError(null);
        try {
            const params = new URLSearchParams();
            if (sourceFilter !== 'all')
                params.set('source', sourceFilter);
            if (hostFilter.trim())
                params.set('target_host', hostFilter.trim());
            if (enabledFilter !== 'all')
                params.set('enabled', enabledFilter);
            const qs = params.toString();
            const res = await fetcher(`/v1/admin/scheduled-tasks${qs ? `?${qs}` : ''}`);
            setTasks(res?.tasks ?? []);
        }
        catch (e) {
            setTasks([]);
            setLoadError(e instanceof Error ? e.message : 'failed to load scheduled tasks');
        }
    }, [fetcher, sourceFilter, hostFilter, enabledFilter]);
    useEffect(() => {
        void loadTasks();
    }, [loadTasks]);
    const fireToast = useCallback((kind, message) => {
        setToast((prev) => ({ kind, message, seq: (prev?.seq ?? 0) + 1 }));
    }, []);
    // Auto-dismiss the toast after 6s.
    useEffect(() => {
        if (!toast)
            return undefined;
        const id = window.setTimeout(() => setToast(null), 6000);
        return () => window.clearTimeout(id);
    }, [toast]);
    // ---- Edit modal ---------------------------------------------------
    const openCreate = useCallback(() => {
        setDraft(emptyDraft());
        setFormBanner(null);
        setFieldErrors({});
        setGeneralErrors([]);
        setEditModal({ kind: 'create' });
    }, []);
    const openEdit = useCallback((row) => {
        setDraft(taskToDraft(row));
        setFormBanner(null);
        setFieldErrors({});
        setGeneralErrors([]);
        setEditModal({ kind: 'edit', row });
    }, []);
    const closeEditModal = useCallback(() => {
        if (submitting)
            return;
        setEditModal({ kind: 'closed' });
        setFormBanner(null);
        setFieldErrors({});
        setGeneralErrors([]);
    }, [submitting]);
    const updateField = useCallback((key, value) => {
        setDraft((d) => ({ ...d, [key]: value }));
        setFieldErrors((fe) => {
            if (!fe[key])
                return fe;
            const next = { ...fe };
            delete next[key];
            return next;
        });
    }, []);
    const applyServerErrors = useCallback((err) => {
        if (err.code === 'INVALID_REQUEST' && Array.isArray(err.errors)) {
            const byField = {};
            const general = [];
            for (const e of err.errors) {
                const key = errorFieldKey(e);
                if (key) {
                    (byField[key] ??= []).push(e.message);
                }
                else {
                    general.push(e.message);
                }
            }
            setFieldErrors(byField);
            setGeneralErrors(general);
            setFormBanner(err.message || 'scheduled task shape failed validation');
            return;
        }
        if (err.status === 409) {
            setFormBanner(err.message || 'conflict: a task with that name already exists');
            return;
        }
        if (err.status === 503) {
            setFormBanner(err.message ||
                'host reconciler unavailable. Refresh to verify whether the edit landed.');
            return;
        }
        setFormBanner(`${err.code}: ${err.message}`);
    }, []);
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (editModal.kind === 'closed')
            return;
        const name = draft.name.trim();
        if (!isValidName(name)) {
            setFieldErrors((fe) => ({
                ...fe,
                name: ['Must match /^[a-z0-9][a-z0-9._-]{1,99}$/i.'],
            }));
            return;
        }
        const envParsed = parseEnvText(draft.envText);
        if (!envParsed.ok) {
            setFieldErrors((fe) => ({ ...fe, envText: [envParsed.error] }));
            return;
        }
        setSubmitting(true);
        setFormBanner(null);
        setFieldErrors({});
        setGeneralErrors([]);
        try {
            if (editModal.kind === 'create') {
                await fetcher('/v1/admin/scheduled-tasks', {
                    method: 'POST',
                    json: draftToCreate(draft, envParsed.env),
                });
                fireToast('success', `Created scheduled task "${name}".`);
            }
            else {
                const patch = draftToPatch(draft, envParsed.env, editModal.row);
                if (Object.keys(patch).length === 0) {
                    setEditModal({ kind: 'closed' });
                    return;
                }
                await fetcher(`/v1/admin/scheduled-tasks/${encodeURIComponent(editModal.row.id)}`, { method: 'PATCH', json: patch });
                const wasDeployManaged = editModal.row.deployScriptManaged;
                fireToast('success', wasDeployManaged
                    ? `Saved "${name}". Ownership flipped to ui — deploy script will skip this row.`
                    : `Saved "${name}".`);
            }
            setEditModal({ kind: 'closed' });
            await loadTasks();
        }
        catch (err) {
            if (err instanceof NexusAdminApiError) {
                applyServerErrors(err);
            }
            else {
                setFormBanner(err instanceof Error ? err.message : 'request failed (unknown error)');
            }
        }
        finally {
            setSubmitting(false);
        }
    }, [applyServerErrors, draft, editModal, fetcher, fireToast, loadTasks]);
    // ---- Enable toggle ------------------------------------------------
    const handleToggleEnabled = useCallback(async (row) => {
        setTogglingIds((prev) => {
            const next = new Set(prev);
            next.add(row.id);
            return next;
        });
        try {
            await fetcher(`/v1/admin/scheduled-tasks/${encodeURIComponent(row.id)}`, { method: 'PATCH', json: { enabled: !row.enabled } });
            fireToast('success', `${!row.enabled ? 'Enabled' : 'Disabled'} "${row.name}".`);
            await loadTasks();
        }
        catch (err) {
            if (err instanceof NexusAdminApiError) {
                fireToast('danger', `${err.code}: ${err.message}`);
            }
            else {
                fireToast('danger', err instanceof Error ? err.message : 'toggle failed (unknown error)');
            }
        }
        finally {
            setTogglingIds((prev) => {
                const next = new Set(prev);
                next.delete(row.id);
                return next;
            });
        }
    }, [fetcher, fireToast, loadTasks]);
    // ---- Run now ------------------------------------------------------
    const openRunModal = useCallback((row) => {
        setRunModal({ kind: 'open', row, submitting: false });
    }, []);
    const closeRunModal = useCallback(() => {
        setRunModal((m) => (m.kind === 'open' && m.submitting ? m : { kind: 'closed' }));
    }, []);
    const handleRunNow = useCallback(async () => {
        if (runModal.kind !== 'open')
            return;
        const row = runModal.row;
        setRunModal({ kind: 'open', row, submitting: true });
        try {
            const res = await fetcher(`/v1/admin/scheduled-tasks/${encodeURIComponent(row.id)}/run-now`, { method: 'POST' });
            if (res.runOk) {
                fireToast('success', `"${row.name}" ran ok in ${formatDuration(res.runDurationMs)}.`);
            }
            else {
                fireToast('danger', `"${row.name}" failed after ${formatDuration(res.runDurationMs)}` +
                    (res.error ? ` — ${res.error}` : ''));
            }
            setRunModal({ kind: 'closed' });
            await loadTasks();
        }
        catch (err) {
            const msg = err instanceof NexusAdminApiError
                ? `${err.code}: ${err.message}`
                : err instanceof Error
                    ? err.message
                    : 'run-now failed (unknown error)';
            fireToast('danger', msg);
            setRunModal({ kind: 'closed' });
        }
    }, [fetcher, fireToast, loadTasks, runModal]);
    // ---- Delete -------------------------------------------------------
    const openDeleteModal = useCallback((row) => {
        setDeleteModal({ kind: 'open', row, submitting: false });
    }, []);
    const closeDeleteModal = useCallback(() => {
        setDeleteModal((m) => m.kind === 'open' && m.submitting ? m : { kind: 'closed' });
    }, []);
    const handleDelete = useCallback(async () => {
        if (deleteModal.kind !== 'open')
            return;
        const row = deleteModal.row;
        setDeleteModal({ kind: 'open', row, submitting: true });
        try {
            await fetcher(`/v1/admin/scheduled-tasks/${encodeURIComponent(row.id)}`, {
                method: 'DELETE',
            });
            fireToast('success', `Deleted "${row.name}".`);
            setDeleteModal({ kind: 'closed' });
            await loadTasks();
        }
        catch (err) {
            const msg = err instanceof NexusAdminApiError
                ? `${err.code}: ${err.message}`
                : err instanceof Error
                    ? err.message
                    : 'delete failed (unknown error)';
            fireToast('danger', msg);
            setDeleteModal({ kind: 'open', row, submitting: false });
        }
    }, [deleteModal, fetcher, fireToast, loadTasks]);
    // ---- Reconcile ----------------------------------------------------
    const openReconcileModal = useCallback(() => {
        setReconcileModal({ kind: 'open' });
    }, []);
    const closeReconcileModal = useCallback(() => {
        if (reconciling)
            return;
        setReconcileModal({ kind: 'closed' });
    }, [reconciling]);
    const handleReconcile = useCallback(async () => {
        setReconciling(true);
        try {
            const res = await fetcher('/v1/admin/scheduled-tasks/reconcile-now', { method: 'POST' });
            setReconcileSummary(res.summary);
            const { installed, updated, unchanged, removed, hostsWithErrors } = res.summary;
            const errCount = hostsWithErrors?.length ?? 0;
            fireToast(errCount > 0 ? 'warning' : 'success', `Reconcile complete: +${installed} ~${updated} =${unchanged} -${removed}` +
                (errCount > 0
                    ? ` (${errCount} host${errCount === 1 ? '' : 's'} with errors)`
                    : ''));
            setReconcileModal({ kind: 'closed' });
            await loadTasks();
        }
        catch (err) {
            const msg = err instanceof NexusAdminApiError
                ? `${err.code}: ${err.message}`
                : err instanceof Error
                    ? err.message
                    : 'reconcile failed (unknown error)';
            fireToast('danger', msg);
        }
        finally {
            setReconciling(false);
        }
    }, [fetcher, fireToast, loadTasks]);
    // ---- Derived ------------------------------------------------------
    const filteredTasks = useMemo(() => {
        if (!tasks)
            return [];
        return [...tasks].sort((a, b) => a.name.localeCompare(b.name));
    }, [tasks]);
    // ---- Render -------------------------------------------------------
    const totalCount = filteredTasks.length;
    const draftNameValid = draft.name === '' || isValidName(draft.name.trim());
    return (_jsxs("div", { children: [_jsx("h1", { className: "h3 mb-3", children: "Scheduled tasks" }), _jsxs("p", { className: "text-muted small mb-4", children: ["Operator surface for the platform's scheduled tasks (systemd timers reconciled from the ", _jsx("code", { children: "scheduled_tasks" }), " table). Edit any row to take ownership; the deploy script will then skip it on the next re-deploy. Hand-laid systemd units on the host are not surfaced here \u2014 only ones managed by Nexus."] }), _jsx("div", { className: "card mb-3", children: _jsx("div", { className: "card-body", children: _jsxs("div", { className: "row g-2 align-items-end", children: [_jsxs("div", { className: "col-md-3", children: [_jsx("label", { className: "form-label small mb-1", children: "Source" }), _jsx("select", { className: "form-select form-select-sm", value: sourceFilter, onChange: (e) => setSourceFilter(e.target.value), children: SOURCE_FILTER_OPTIONS.map((o) => (_jsx("option", { value: o.value, children: o.label }, o.value))) })] }), _jsxs("div", { className: "col-md-3", children: [_jsx("label", { className: "form-label small mb-1", children: "Target host" }), _jsx("input", { type: "text", className: "form-control form-control-sm", placeholder: "exact match", value: hostFilter, onChange: (e) => setHostFilter(e.target.value), spellCheck: false, autoComplete: "off" })] }), _jsxs("div", { className: "col-md-2", children: [_jsx("label", { className: "form-label small mb-1", children: "Enabled" }), _jsxs("select", { className: "form-select form-select-sm", value: enabledFilter, onChange: (e) => setEnabledFilter(e.target.value), children: [_jsx("option", { value: "all", children: "all" }), _jsx("option", { value: "true", children: "true" }), _jsx("option", { value: "false", children: "false" })] })] }), _jsxs("div", { className: "col-md-4 d-flex justify-content-end gap-2", children: [_jsx("button", { type: "button", className: "btn btn-sm btn-outline-secondary", onClick: () => void loadTasks(), disabled: tasks === null, children: "Refresh" }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-primary", onClick: openReconcileModal, disabled: reconciling, title: "Apply pending changes to all target hosts.", children: reconciling ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Reconciling\u2026"] })) : ('Reconcile now') }), _jsx("button", { type: "button", className: "btn btn-sm btn-primary", onClick: openCreate, children: "New task" })] })] }) }) }), toast && (_jsxs("div", { className: `alert alert-${toast.kind} d-flex justify-content-between align-items-center`, children: [_jsx("span", { children: toast.message }), _jsx("button", { type: "button", className: "btn-close", "aria-label": "Dismiss", onClick: () => setToast(null) })] })), reconcileSummary && (_jsx("div", { className: "card mb-3 border-info", children: _jsx("div", { className: "card-body", children: _jsxs("div", { className: "d-flex justify-content-between align-items-start", children: [_jsxs("div", { children: [_jsx("div", { className: "fw-semibold mb-1", children: "Last reconcile summary" }), _jsxs("div", { className: "small text-muted", children: ["installed ", _jsx("strong", { children: reconcileSummary.installed }), " \u00B7 updated ", _jsx("strong", { children: reconcileSummary.updated }), " \u00B7 unchanged ", _jsx("strong", { children: reconcileSummary.unchanged }), " \u00B7 removed ", _jsx("strong", { children: reconcileSummary.removed })] }), reconcileSummary.hostsWithErrors.length > 0 && (_jsxs("div", { className: "small text-danger mt-1", children: ["Hosts with errors:", ' ', reconcileSummary.hostsWithErrors.map((h, i) => (_jsxs("span", { children: [_jsx("code", { children: h }), i < reconcileSummary.hostsWithErrors.length - 1
                                                        ? ', '
                                                        : ''] }, h)))] }))] }), _jsx("button", { type: "button", className: "btn btn-sm btn-link text-muted", onClick: () => setReconcileSummary(null), children: "Dismiss" })] }) }) })), _jsxs("div", { className: "card", children: [_jsxs("div", { className: "card-header d-flex justify-content-between align-items-center", children: [_jsx("span", { className: "fw-semibold", children: "Scheduled tasks" }), _jsx("span", { className: "text-muted small", children: tasks === null
                                    ? 'Loading…'
                                    : `${totalCount} row${totalCount === 1 ? '' : 's'}` })] }), tasks === null && (_jsxs("div", { className: "card-body text-muted small", children: [_jsx("div", { className: "spinner-border spinner-border-sm me-2", role: "status" }), "Loading scheduled tasks\u2026"] })), tasks !== null && loadError !== null && (_jsxs("div", { className: "card-body", children: [_jsx("div", { className: "alert alert-danger small mb-2", children: loadError }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-danger", onClick: () => void loadTasks(), children: "Retry" })] })), tasks !== null && loadError === null && filteredTasks.length === 0 && (_jsxs("div", { className: "card-body text-center text-muted", children: [_jsx("p", { className: "mb-2", children: "No scheduled tasks match the current filters." }), _jsx("button", { type: "button", className: "btn btn-sm btn-primary", onClick: openCreate, children: "New task" })] })), tasks !== null && loadError === null && filteredTasks.length > 0 && (_jsx("div", { className: "table-responsive", children: _jsxs("table", { className: "table table-striped table-hover mb-0 align-middle", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { scope: "col", children: "Name" }), _jsx("th", { scope: "col", children: "Schedule" }), _jsx("th", { scope: "col", children: "Target host" }), _jsx("th", { scope: "col", children: "State" }), _jsx("th", { scope: "col", children: "Owner" }), _jsx("th", { scope: "col", children: "Last run" }), _jsx("th", { scope: "col", className: "text-end", children: "Actions" })] }) }), _jsx("tbody", { children: filteredTasks.map((t) => {
                                        const state = deriveState(t, now);
                                        const toggling = togglingIds.has(t.id);
                                        return (_jsxs("tr", { children: [_jsx("td", { children: _jsx("button", { type: "button", className: "btn btn-link p-0 text-decoration-none align-baseline", onClick: () => openEdit(t), title: t.description ?? undefined, children: t.name }) }), _jsx("td", { className: "small", children: _jsx("code", { children: t.schedule }) }), _jsx("td", { className: "small", children: t.targetHost }), _jsx("td", { children: _jsx(StateBadgeView, { state: state }) }), _jsxs("td", { children: [_jsx(OwnerCell, { source: t.source }), t.source === 'deploy_script' && !t.deployScriptManaged && (_jsx("div", { className: "form-text small", children: "(manual edit \u2014 ownership flipped)" }))] }), _jsx("td", { className: "small", children: t.lastRunAt === null ? (_jsx("span", { className: "text-muted", children: "never" })) : (_jsxs("span", { title: t.lastRunAt, children: [formatRelative(t.lastRunAt, now), ' ', t.lastRunOk === true && (_jsx("span", { className: "badge bg-success ms-1", children: "ok" })), t.lastRunOk === false && (_jsx("span", { className: "badge bg-danger ms-1", title: t.lastRunError ?? undefined, children: "fail" })), t.lastRunOk === null && (_jsx("span", { className: "badge bg-info text-dark ms-1", children: "running" })), t.lastRunDurationMs !== null && (_jsxs("span", { className: "text-muted ms-1", children: ["(", formatDuration(t.lastRunDurationMs), ")"] }))] })) }), _jsx("td", { className: "text-end", children: _jsxs("div", { className: "btn-group btn-group-sm", role: "group", children: [_jsx("button", { type: "button", className: "btn btn-outline-primary", onClick: () => openRunModal(t), title: "Run this task now", children: "Run now" }), _jsx("button", { type: "button", className: t.enabled
                                                                    ? 'btn btn-outline-secondary'
                                                                    : 'btn btn-outline-success', onClick: () => void handleToggleEnabled(t), disabled: toggling, title: t.enabled ? 'Disable' : 'Enable', children: toggling ? (_jsx("span", { className: "spinner-border spinner-border-sm", role: "status", "aria-hidden": "true" })) : t.enabled ? ('Disable') : ('Enable') }), _jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: () => openEdit(t), title: "Edit", "aria-label": "Edit", children: _jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 16 16", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325" }) }) }), _jsx("button", { type: "button", className: "btn btn-outline-danger", onClick: () => openDeleteModal(t), title: "Delete", "aria-label": "Delete", children: _jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 16 16", fill: "currentColor", "aria-hidden": "true", children: [_jsx("path", { d: "M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" }), _jsx("path", { d: "M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" })] }) })] }) })] }, t.id));
                                    }) })] }) }))] }), editModal.kind !== 'closed' && (_jsx(TaskEditModal, { mode: editModal.kind, original: editModal.kind === 'edit' ? editModal.row : null, draft: draft, fieldErrors: fieldErrors, generalErrors: generalErrors, formBanner: formBanner, submitting: submitting, draftNameValid: draftNameValid, onChange: updateField, onCancel: closeEditModal, onSubmit: handleSubmit })), runModal.kind === 'open' && (_jsxs(ConfirmModal, { title: "Run scheduled task", confirmLabel: "Run now", confirmVariant: "primary", submitting: runModal.submitting, onCancel: closeRunModal, onConfirm: () => void handleRunNow(), children: [_jsxs("p", { className: "mb-2", children: ["Run task ", _jsx("code", { children: runModal.row.name }), " on", ' ', _jsx("code", { children: runModal.row.targetHost }), " now?"] }), _jsxs("p", { className: "small text-muted mb-0", children: ["Last run:", ' ', runModal.row.lastRunAt
                                ? `${formatRelative(runModal.row.lastRunAt, now)} (${runModal.row.lastRunOk === true
                                    ? 'ok'
                                    : runModal.row.lastRunOk === false
                                        ? 'fail'
                                        : 'running'})`
                                : 'never', "."] })] })), deleteModal.kind === 'open' && (_jsxs(ConfirmModal, { title: "Delete scheduled task", confirmLabel: "Delete", confirmVariant: "danger", submitting: deleteModal.submitting, onCancel: closeDeleteModal, onConfirm: () => void handleDelete(), children: [_jsxs("p", { className: "mb-2", children: ["Delete task ", _jsx("code", { children: deleteModal.row.name }), "?"] }), _jsxs("p", { className: "small text-muted mb-0", children: ["The reconciler will remove the systemd unit on", ' ', _jsx("code", { children: deleteModal.row.targetHost }), " on its next pass.", deleteModal.row.deployScriptManaged && (_jsxs(_Fragment, { children: [' ', "This row was last touched by the deploy script \u2014 the next re-deploy will re-install it unless removed from the deploy manifest."] }))] })] })), reconcileModal.kind === 'open' && (_jsx(ConfirmModal, { title: "Reconcile scheduled tasks", confirmLabel: "Reconcile", confirmVariant: "primary", submitting: reconciling, onCancel: closeReconcileModal, onConfirm: () => void handleReconcile(), children: _jsxs("p", { className: "mb-0", children: ["Apply pending scheduled-task changes across all target hosts? The reconciler will install / update / remove systemd units to match the current rows in ", _jsx("code", { children: "scheduled_tasks" }), "."] }) }))] }));
}
function TaskEditModal({ mode, original, draft, fieldErrors, generalErrors, formBanner, submitting, draftNameValid, onChange, onCancel, onSubmit, }) {
    const ownershipNote = mode === 'edit' && original?.deployScriptManaged ? (_jsxs("div", { className: "alert alert-info small mb-3", children: ["This row is currently owned by the ", _jsx("strong", { children: "deploy script" }), ". Saving any change will flip ownership to ", _jsx("strong", { children: "ui" }), " \u2014 the deploy script will skip this row on its next re-deploy."] })) : null;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "modal show", style: { display: 'block' }, role: "dialog", "aria-modal": "true", children: _jsx("div", { className: "modal-dialog modal-dialog-centered modal-lg", children: _jsx("div", { className: "modal-content", children: _jsxs("form", { onSubmit: onSubmit, noValidate: true, children: [_jsxs("div", { className: "modal-header", children: [_jsx("h5", { className: "modal-title", children: mode === 'create' ? 'New scheduled task' : 'Edit scheduled task' }), _jsx("button", { type: "button", className: "btn-close", "aria-label": "Close", onClick: onCancel, disabled: submitting })] }), _jsxs("div", { className: "modal-body", children: [ownershipNote, formBanner && (_jsx("div", { className: "alert alert-danger", children: formBanner })), generalErrors.length > 0 && (_jsxs("div", { className: "alert alert-warning", children: [_jsx("div", { className: "fw-semibold mb-1", children: "Server validation errors:" }), _jsx("ul", { className: "mb-0 small", children: generalErrors.map((m, i) => (_jsx("li", { children: m }, i))) })] })), _jsxs(Field, { label: "Name", required: true, errors: fieldErrors.name, hint: "Stable identifier. Must match /^[a-z0-9][a-z0-9._-]{1,99}$/i. Used as the systemd unit name.", children: [_jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.name ?? (!draftNameValid ? ['invalid'] : undefined)), value: draft.name, onChange: (e) => onChange('name', e.target.value), autoComplete: "off", spellCheck: false, pattern: NAME_RE.source, maxLength: 100 }), !draftNameValid && !fieldErrors.name && (_jsx("div", { className: "invalid-feedback d-block small", children: "Must match the server regex (length 2-100, starts alphanumeric, then a-z 0-9 . _ -)." }))] }), _jsx(Field, { label: "Schedule", required: true, errors: fieldErrors.schedule, hint: "Raw systemd OnCalendar expression (e.g. *-*-* 03:00:00, daily, Mon..Fri *-*-* 09:00).", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.schedule), value: draft.schedule, onChange: (e) => onChange('schedule', e.target.value), autoComplete: "off", spellCheck: false }) }), _jsx(Field, { label: "Command", required: true, errors: fieldErrors.command, hint: "ExecStart line. Use absolute paths; the shell is not invoked.", children: _jsx("textarea", { className: fieldClass('form-control font-monospace', fieldErrors.command), rows: 2, value: draft.command, onChange: (e) => onChange('command', e.target.value), spellCheck: false }) }), _jsx(Field, { label: "Target host", required: true, errors: fieldErrors.targetHost, hint: "Host the reconciler will install this unit on (SSH alias or hostname).", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.targetHost), value: draft.targetHost, onChange: (e) => onChange('targetHost', e.target.value), autoComplete: "off", spellCheck: false }) }), _jsx(Field, { label: "Working directory", errors: fieldErrors.workingDirectory, hint: "Optional. Sets systemd WorkingDirectory= on the unit.", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.workingDirectory), value: draft.workingDirectory, onChange: (e) => onChange('workingDirectory', e.target.value), autoComplete: "off", spellCheck: false, placeholder: "e.g. /var/lib/myapp" }) }), _jsx(Field, { label: "Environment", errors: fieldErrors.envText, hint: "One KEY=VALUE per line. Blank lines and lines starting with # are ignored.", children: _jsx("textarea", { className: fieldClass('form-control font-monospace', fieldErrors.envText), rows: 4, value: draft.envText, onChange: (e) => onChange('envText', e.target.value), spellCheck: false, placeholder: 'NODE_ENV=production\nFOO=bar' }) }), _jsx(Field, { label: "User", errors: fieldErrors.userName, hint: "Optional. Sets systemd User= on the unit (defaults to nexus).", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.userName), value: draft.userName, onChange: (e) => onChange('userName', e.target.value), autoComplete: "off", spellCheck: false }) }), _jsx(Field, { label: "Description", errors: fieldErrors.description, hint: "Optional. Human-readable description shown on this page.", children: _jsx("textarea", { className: fieldClass('form-control', fieldErrors.description), rows: 2, value: draft.description, onChange: (e) => onChange('description', e.target.value), spellCheck: true }) }), _jsxs("div", { className: "form-check mt-2", children: [_jsx("input", { type: "checkbox", className: "form-check-input", id: "scheduled-task-enabled", checked: draft.enabled, onChange: (e) => onChange('enabled', e.target.checked) }), _jsx("label", { className: "form-check-label small", htmlFor: "scheduled-task-enabled", children: "Enabled \u2014 when on, the reconciler installs + starts the systemd timer." })] })] }), _jsxs("div", { className: "modal-footer", children: [_jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: onCancel, disabled: submitting, children: "Cancel" }), _jsx("button", { type: "submit", className: "btn btn-primary", disabled: submitting, children: submitting ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Saving\u2026"] })) : mode === 'create' ? ('Create') : ('Save') })] })] }) }) }) }), _jsx("div", { className: "modal-backdrop show" })] }));
}
function ConfirmModal({ title, confirmLabel, confirmVariant, submitting, onCancel, onConfirm, children, }) {
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "modal show", style: { display: 'block' }, role: "dialog", "aria-modal": "true", children: _jsx("div", { className: "modal-dialog modal-dialog-centered", children: _jsxs("div", { className: "modal-content", children: [_jsxs("div", { className: "modal-header", children: [_jsx("h5", { className: "modal-title", children: title }), _jsx("button", { type: "button", className: "btn-close", "aria-label": "Close", onClick: onCancel, disabled: submitting })] }), _jsx("div", { className: "modal-body", children: children }), _jsxs("div", { className: "modal-footer", children: [_jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: onCancel, disabled: submitting, children: "Cancel" }), _jsx("button", { type: "button", className: `btn btn-${confirmVariant}`, onClick: onConfirm, disabled: submitting, children: submitting ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Working\u2026"] })) : (confirmLabel) })] })] }) }) }), _jsx("div", { className: "modal-backdrop show" })] }));
}
// ---------------------------------------------------------------------
// Reusable form helpers (same shape as RateCardsPage).
function fieldClass(base, errs) {
    return errs && errs.length > 0 ? `${base} is-invalid` : base;
}
function Field({ label, required, errors, hint, children }) {
    return (_jsxs("div", { className: "mb-3", children: [_jsxs("label", { className: "form-label mb-1", children: [label, required && _jsx("span", { className: "text-danger ms-1", children: "*" })] }), children, hint && !errors?.length && _jsx("div", { className: "form-text small", children: hint }), errors?.map((m, i) => (_jsx("div", { className: "invalid-feedback d-block small", children: m }, i)))] }));
}
//# sourceMappingURL=ScheduledTasksPage.js.map